import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Col, Input, Label, Row, Spinner, Table } from "reactstrap";
import DatePicker from "react-datepicker";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { toast } from "react-toastify";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import FixedSave from "../../../../components/Actions/FixedSave";
import sidebar from "../../utils/sidebar";
import { post } from "../../../../utils/helpers/api";
import { useSettings } from "../../../../context/settings";
import { formatMoney } from "../../utils/payrollStatus";

const toDateString = (value) => (value ? format(new Date(value), 'yyyy-MM-dd') : null);

function CreatePayrollRunPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { datePickerFormat, settings } = useSettings();

  const [runData, setRunData] = useState({
    name: '',
    period_start: startOfMonth(new Date()),
    period_end: endOfMonth(new Date()),
    payment_date: endOfMonth(new Date()),
    notes: ''
  });
  const [payslips, setPayslips] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  const currency = settings?.sales?.base_currency || '';

  const applyMonth = (date) => {
    setRunData({
      ...runData,
      period_start: startOfMonth(date),
      period_end: endOfMonth(date),
      payment_date: endOfMonth(date)
    });
    setPayslips(null);
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    const data = await post('/hr/payroll-runs/preview', {
      period_start: toDateString(runData.period_start),
      period_end: toDateString(runData.period_end)
    });
    if (data?.success) {
      setPayslips(data.payslips);
      setExcluded([]);
      if (!data.payslips.length) {
        toast.info(t('hr.payrollRun.noEligibleEmployees'));
      }
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setPreviewLoading(false);
  };

  const toggleEmployee = (employeeId) => {
    setExcluded(excluded.includes(employeeId)
      ? excluded.filter(id => id !== employeeId)
      : [...excluded, employeeId]);
  };

  const includedPayslips = (payslips || []).filter(p => !excluded.includes(p.employee_id));

  const includedTotals = includedPayslips.reduce((acc, p) => ({
    basic: acc.basic + Number(p.basic_salary || 0),
    gross: acc.gross + Number(p.gross_salary || 0),
    deductions: acc.deductions + Number(p.total_deductions || 0),
    net: acc.net + Number(p.net_salary || 0)
  }), { basic: 0, gross: 0, deductions: 0, net: 0 });

  const savePayrollRun = async () => {
    setSaveLoading(true);
    const data = await post('/hr/payroll-runs/store', {
      name: runData.name || null,
      period_start: toDateString(runData.period_start),
      period_end: toDateString(runData.period_end),
      payment_date: toDateString(runData.payment_date),
      currency: currency || null,
      notes: runData.notes || null,
      employee_ids: includedPayslips.map(p => p.employee_id)
    });
    if (data?.success) {
      toast.success(t('hr.payrollRun.created'));
      navigate(`/hr/payroll/view/${data.payroll_run.id}`);
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('hr.payroll'), t('hr.payrollRun.newRun')]}
          onBack={() => navigate('/hr/payroll')}
        />

        <div className="subheader-page-content scrollable">
          <Card className="mb-3">
            <CardBody>
              <h5 className="fw-bold mb-3">{t('hr.payrollRun.period')}</h5>

              <div className="d-flex gap-2 mb-3 flex-wrap">
                <DefaultButton size="sm" color="secondary" outline onClick={() => applyMonth(new Date())}>
                  {t('common.thisMonth')}
                </DefaultButton>
                <DefaultButton size="sm" color="secondary" outline onClick={() => applyMonth(subMonths(new Date(), 1))}>
                  {t('common.lastMonth')}
                </DefaultButton>
              </div>

              <Row>
                <Col md="3" className="mb-3">
                  <Label>{t('common.from')}</Label>
                  <DatePicker
                    selected={runData.period_start}
                    onChange={(date) => setRunData({ ...runData, period_start: date })}
                    className="form-control"
                    dateFormat={datePickerFormat}
                  />
                </Col>
                <Col md="3" className="mb-3">
                  <Label>{t('common.to')}</Label>
                  <DatePicker
                    selected={runData.period_end}
                    onChange={(date) => setRunData({ ...runData, period_end: date })}
                    className="form-control"
                    dateFormat={datePickerFormat}
                  />
                </Col>
                <Col md="3" className="mb-3">
                  <Label>{t('hr.payrollRun.paymentDate')}</Label>
                  <DatePicker
                    selected={runData.payment_date}
                    onChange={(date) => setRunData({ ...runData, payment_date: date })}
                    className="form-control"
                    dateFormat={datePickerFormat}
                  />
                </Col>
                <Col md="3" className="mb-3">
                  <Label>{t('common.name')}</Label>
                  <Input
                    value={runData.name}
                    onChange={(e) => setRunData({ ...runData, name: e.target.value })}
                    placeholder={t('hr.payrollRun.namePlaceholder')}
                  />
                </Col>
              </Row>

              <DefaultButton
                color="primary"
                size="sm"
                onClick={loadPreview}
                loading={previewLoading}
                disabled={previewLoading || !runData.period_start || !runData.period_end}
              >
                {t('hr.payrollRun.calculate')}
              </DefaultButton>
            </CardBody>
          </Card>

          {previewLoading && <Spinner color="primary" className="d-block mx-auto my-4" />}

          {payslips && !previewLoading && (
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <h5 className="fw-bold mb-0">
                    {t('hr.payrollRun.employees')}
                    <span className="text-muted fw-normal ms-2">
                      {includedPayslips.length} / {payslips.length}
                    </span>
                  </h5>
                </div>

                <Table responsive hover className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th>{t('hr.employeeName')}</th>
                      <th className="text-end">{t('hr.basicSalary')}</th>
                      <th className="text-center">{t('hr.payrollRun.paidDays')}</th>
                      <th className="text-end">{t('hr.benefits.totalAllowances')}</th>
                      <th className="text-end">{t('hr.benefits.totalDeductions')}</th>
                      <th className="text-end">{t('hr.benefits.netSalary')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payslips.map((payslip) => {
                      const included = !excluded.includes(payslip.employee_id);
                      return (
                        <tr key={payslip.employee_id} className={included ? '' : 'text-muted'}>
                          <td>
                            <Input
                              type="checkbox"
                              className="mt-0"
                              checked={included}
                              onChange={() => toggleEmployee(payslip.employee_id)}
                            />
                          </td>
                          <td>
                            <div className="fw-semibold">{payslip.employee_name}</div>
                            <div className="small text-muted">
                              {[payslip.job_title, payslip.department].filter(Boolean).join(' · ') || '—'}
                            </div>
                          </td>
                          <td className="text-end">{formatMoney(payslip.basic_salary)}</td>
                          <td className="text-center">
                            {payslip.paid_days} / {payslip.period_days}
                            {(Number(payslip.absent_days) > 0 || Number(payslip.unpaid_leave_days) > 0) && (
                              <div className="small text-danger">
                                {Number(payslip.absent_days) > 0 && `${payslip.absent_days} ${t('hr.payrollRun.absentShort')}`}
                                {Number(payslip.absent_days) > 0 && Number(payslip.unpaid_leave_days) > 0 && ' · '}
                                {Number(payslip.unpaid_leave_days) > 0 && `${payslip.unpaid_leave_days} ${t('hr.payrollRun.unpaidShort')}`}
                              </div>
                            )}
                          </td>
                          <td className="text-end text-success">+{formatMoney(payslip.total_allowances)}</td>
                          <td className="text-end text-danger">-{formatMoney(payslip.total_deductions)}</td>
                          <td className="text-end fw-bold">{formatMoney(payslip.net_salary)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                <div className="border rounded p-3 mt-3">
                  <div className="d-flex justify-content-between small mb-2">
                    <span className="text-muted">{t('hr.payrollRun.totalBasic')}</span>
                    <span>{formatMoney(includedTotals.basic, currency)}</span>
                  </div>
                  <div className="d-flex justify-content-between small mb-2">
                    <span className="text-muted">{t('hr.benefits.grossSalary')}</span>
                    <span>{formatMoney(includedTotals.gross, currency)}</span>
                  </div>
                  <div className="d-flex justify-content-between small mb-2">
                    <span className="text-muted">{t('hr.benefits.totalDeductions')}</span>
                    <span className="text-danger">-{formatMoney(includedTotals.deductions, currency)}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between fw-bold fs-5">
                    <span>{t('hr.payrollRun.totalPayout')}</span>
                    <span>{formatMoney(includedTotals.net, currency)}</span>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      <FixedSave
        onSave={savePayrollRun}
        loading={saveLoading}
        disabled={saveLoading || !includedPayslips.length}
      />
    </SidebarLayout>
  );
}

export default CreatePayrollRunPage;
